import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import UserManagementTab from "@/components/UserManagementTab";
import SystemHealthPanel from "@/components/SystemHealthPanel";
import { toast } from "sonner";
import { Plus, Trash2, Calendar, HandCoins, Send, Shield, CheckCircle2, LinkIcon, Unlink, AlertTriangle, TestTube2, RefreshCw, BookOpen, ArrowUp, ArrowDown, Pencil, ExternalLink, UsersRound, Receipt, Folder, Database, Landmark, PlugZap, ArrowRight } from "lucide-react";

const SERVICE_META = {
  mailchimp: { label: "Mailchimp", icon: Send, fields: [
    { key: "api_key", label: "API Key", placeholder: "paste the full key — ends with '-us10' or similar", sensitive: true },
    { key: "list_id", label: "Audience ID", placeholder: "abc1234567 (Audience → Settings → Audience name & defaults)" },
  ],
  helper: "Get the API key in Mailchimp → Account & billing → Extras → API keys. The server prefix (e.g. `us10`) is parsed automatically from the end of the key — you don't need to set it separately. Audience ID is under Audience → Settings → Audience name & defaults → 'Unique ID for audience'.",
  test_endpoint: "/integrations/mailchimp/test",
  },
  esv_api: { label: "ESV Bible API", icon: BookOpen, fields: [
    { key: "api_token", label: "API Token", placeholder: "••••••••", sensitive: true },
  ], helper: "Get a free token at api.esv.org/account/create-application/ (Commercial: No). Paste it above, enable the switch, and Save. Powers the Home dashboard's Daily Devotional scripture text." },
  ccb: {
    label: "Pushpay ChMS (Church Community Builder)",
    icon: UsersRound,
    fields: [
      { key: "subdomain", label: "Subdomain", placeholder: "familychapel or familychapel.ccbchurch.com" },
      { key: "username", label: "API Username", placeholder: "api-user" },
      { key: "password", label: "API Password", placeholder: "••••••••", sensitive: true },
    ],
    helper: "Enables the People Lookup page. Create a dedicated API user inside CCB (Settings → API Users) with 'Create/Edit Individuals' + 'Search Individuals' permissions. We only use it for read-only individual_search calls.",
    test_endpoint: "/integrations/ccb/test",
  },
};

export default function Settings() {
  const { user: me } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [users, setUsers] = useState([]);
  const [links, setLinks] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [newLink, setNewLink] = useState({ title: "", url: "", icon: "", category: "" });
  const [calStatus, setCalStatus] = useState(null);
  const [calendarIdsText, setCalendarIdsText] = useState("");
  const [receiptStatus, setReceiptStatus] = useState(null);
  const [receiptFolderInput, setReceiptFolderInput] = useState("");
  const [savingReceiptFolder, setSavingReceiptFolder] = useState(false);
  // OAuth diagnostic — surfaces the live redirect_uri so admins can sanity-
  // check it against what's whitelisted in Google Cloud Console without
  // hitting DevTools. Populated lazily by the "Show OAuth config" button.
  const [oauthDebug, setOauthDebug] = useState(null);
  // Live state of the durable Google credential record — populated lazily
  // by the "Show connection state" button. This is the production-side
  // bridge for diagnosing disconnect issues without running mongo queries.
  const [oauthState, setOauthState] = useState(null);
  const [oauthStateLoading, setOauthStateLoading] = useState(false);
  const [forceRefreshResult, setForceRefreshResult] = useState(null);
  const [forceRefreshLoading, setForceRefreshLoading] = useState(false);

  const loadOauthDebug = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/debug");
      setOauthDebug(data);
    } catch (e) {
      toast.error("Unable to load OAuth diagnostic");
    }
  };

  const loadOauthState = async () => {
    setOauthStateLoading(true);
    try {
      const { data } = await api.get("/admin/google-oauth/state");
      setOauthState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to load OAuth state");
    } finally {
      setOauthStateLoading(false);
    }
  };

  const forceRefresh = async () => {
    setForceRefreshLoading(true);
    setForceRefreshResult(null);
    try {
      const { data } = await api.post("/admin/google-oauth/force-refresh");
      setForceRefreshResult(data);
      // Refresh the static state panel too so before/after match.
      loadOauthState();
      // And tile state.
      const cs = await api.get("/calendar/google/status");
      setCalStatus(cs.data);
    } catch (e) {
      setForceRefreshResult({ result: "request_failed", error: e?.response?.data?.detail ?? e.message });
    } finally {
      setForceRefreshLoading(false);
    }
  };

  const copyOauthState = async () => {
    if (!oauthState) return;
    const blob = JSON.stringify({ state: oauthState, force_refresh: forceRefreshResult }, null, 2);
    try {
      await navigator.clipboard.writeText(blob);
      toast.success("Copied to clipboard");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = blob;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Copied to clipboard"); }
      catch { toast.error("Copy failed — select the JSON manually"); }
      document.body.removeChild(ta);
    }
  };

  const loadAll = async () => {
    const [i, u, l, cs, rs] = await Promise.all([
      api.get("/integrations"),
      api.get("/users?include_inactive=true"),
      api.get("/quick-links"),
      api.get("/calendar/google/status"),
      api.get("/receipts/storage/status"),
    ]);
    setIntegrations(i.data);
    setUsers(u.data);
    setLinks(l.data);
    setCalStatus(cs.data);
    setCalendarIdsText((cs.data.calendar_ids || []).join("\n"));
    setReceiptStatus(rs.data);
  };
  useEffect(() => {
    loadAll();
    // Show toasts if redirected back from OAuth callback
    const p = new URLSearchParams(window.location.search);
    if (p.get("calendar_connected")) {
      if (p.get("drive_scope_missing")) {
        toast.warning("Calendar connected, but Drive permission was dropped. Add the drive.file scope in Google Cloud Console → OAuth consent screen and reconnect.");
      } else {
        toast.success("Google Calendar connected");
      }
      window.history.replaceState({}, "", "/settings");
    } else if (p.get("calendar_error")) {
      toast.error(`Google Calendar error: ${p.get("calendar_error")}`);
      window.history.replaceState({}, "", "/settings");
    }
  }, []);

  const connectCalendar = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/login");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to start OAuth");
    }
  };

  const disconnectCalendar = async () => {
    if (!confirm("Disconnect Google Calendar? Events will stop loading until reconnected.")) return;
    try {
      await api.post("/oauth/calendar/disconnect");
      toast.success("Google Calendar disconnected");
      loadAll();
    } catch {
      toast.error("Unable to disconnect");
    }
  };

  const saveCalendarIds = async () => {
    const ids = calendarIdsText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    try {
      await api.put("/calendar/google/calendar-ids", { calendar_ids: ids });
      toast.success("Calendar list saved");
      loadAll();
    } catch {
      toast.error("Unable to save calendar IDs");
    }
  };

  const saveReceiptFolder = async () => {
    if (!receiptFolderInput.trim()) {
      toast.error("Paste the Drive folder URL or its ID first");
      return;
    }
    setSavingReceiptFolder(true);
    try {
      const { data } = await api.put("/receipts/storage/folder", {
        folder_url_or_id: receiptFolderInput.trim(),
      });
      toast.success(`Receipts folder set: ${data.parent_folder_name}`);
      setReceiptFolderInput("");
      loadAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Unable to save folder";
      toast.error(msg);
    } finally {
      setSavingReceiptFolder(false);
    }
  };

  const updateIntegration = async (service, patch) => {
    const current = integrations.find((x) => x.service === service);
    const updated = { ...current, ...patch, service };
    try {
      const { data } = await api.put(`/integrations/${service}`, updated);
      // Confirm persistence: optimistically use the server's response (source of
      // truth after the credential-preserve merge), then re-fetch to make sure
      // every card in the UI reflects the live DB state.
      setIntegrations((rows) => {
        const next = rows.slice();
        const i = next.findIndex((r) => r.service === service);
        if (i === -1) next.push(data); else next[i] = data;
        return next;
      });
      toast.success(`${SERVICE_META[service]?.label || service} saved`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to save settings");
    }
  };

  const clearCredentials = async (service) => {
    try {
      await api.delete(`/integrations/${service}/credentials?confirm=1`);
      toast.success(`${SERVICE_META[service]?.label || service} credentials cleared`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to clear credentials");
    }
  };

  const setConfig = (svc, key, value) => {
    const current = integrations.find((x) => x.service === svc);
    updateIntegration(svc, { config: { ...(current?.config ?? {}), [key]: value } });
  };

  const changeRole = async (user_id, role) => {
    try {
      await api.patch(`/users/${user_id}/role`, { role });
      toast.success("Role updated");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to update role");
    }
  };

  const toggleKpiEdit = async (user_id, value) => {
    try {
      await api.patch(`/users/${user_id}/permissions`, { can_edit_kpi: value });
      toast.success(value ? "KPI edit granted" : "KPI edit revoked");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to update permissions");
    }
  };

  const toggleNewsletterEditor = async (user_id, value) => {
    try {
      await api.patch(`/users/${user_id}/permissions`, { newsletter_editor: value });
      toast.success(value ? "Newsletter editor granted" : "Newsletter editor revoked");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to update permissions");
    }
  };

  const addLink = async () => {
    if (!newLink.title || !newLink.url) {
      toast.error("Title and URL required");
      return;
    }
    try {
      await api.post("/quick-links", newLink);
      toast.success("Link added");
      setLinkOpen(false);
      setNewLink({ title: "", url: "", icon: "", category: "" });
      loadAll();
    } catch {
      toast.error("Unable to add link");
    }
  };

  const saveLinkEdit = async (id, patch) => {
    try {
      await api.patch(`/quick-links/${id}`, patch);
      toast.success("Link updated");
      loadAll();
    } catch {
      toast.error("Unable to update link");
    }
  };

  const moveLink = async (id, direction) => {
    const idx = links.findIndex((l) => l.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= links.length) return;
    const reordered = [...links];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setLinks(reordered); // optimistic
    try {
      await api.post("/quick-links/reorder", { ordered_ids: reordered.map((l) => l.id) });
    } catch {
      toast.error("Unable to reorder");
      loadAll();
    }
  };

  const delLink = async (id) => {
    try {
      await api.delete(`/quick-links/${id}`);
      toast.success("Removed");
      loadAll();
    } catch {
      toast.error("Unable to remove");
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <Tabs defaultValue="integrations" className="space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList className="bg-white border border-brand-primary/10 rounded-full p-1 h-auto">
            <TabsTrigger value="integrations" data-testid="tab-integrations" className="rounded-full data-[state=active]:bg-terracotta data-[state=active]:text-white px-5 py-1.5">Integrations</TabsTrigger>
            <TabsTrigger value="roles" data-testid="tab-roles" className="rounded-full data-[state=active]:bg-terracotta data-[state=active]:text-white px-5 py-1.5">Staff & Roles</TabsTrigger>
            <TabsTrigger value="links" data-testid="tab-links" className="rounded-full data-[state=active]:bg-terracotta data-[state=active]:text-white px-5 py-1.5">Quick Links</TabsTrigger>
          </TabsList>
          <Link
            to="/admin/kpi-diagnostics"
            data-testid="link-kpi-diagnostics"
            className="text-xs text-terracotta hover:text-terracotta-hover hover:underline inline-flex items-center gap-1.5 transition-colors"
          >
            <Database className="h-3.5 w-3.5" /> KPI diagnostics
          </Link>
        </div>

        {/* Integrations */}
        <TabsContent value="integrations" className="space-y-5">
          <SystemHealthPanel />

          {/* Google Calendar (OAuth) */}
          <Card data-testid="integration-card-google_calendar" className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
            <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-terracotta/10 grid place-items-center">
                  <Calendar className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
                </div>
                <div>
                  <p className="font-heading text-lg text-brand-primary">Google Calendar</p>
                  <p className="text-sm text-brand-primary/60">
                    {calStatus?.connected
                      ? <>Connected as <span className="text-brand-primary">{calStatus.connected_email ?? "a Google account"}</span></>
                      : "Not connected — calendar page will prompt until this is linked."}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {calStatus?.connected ? (
                  <>
                    <Badge className="rounded-full bg-sage/20 text-brand-primary border-0">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Connected
                    </Badge>
                    <Button
                      variant="outline"
                      data-testid="calendar-reconnect-button"
                      onClick={connectCalendar}
                      className="rounded-full border-brand-primary/15"
                    >
                      <LinkIcon className="h-4 w-4 mr-1.5" /> Reconnect
                    </Button>
                    <Button
                      variant="outline"
                      data-testid="calendar-disconnect-button"
                      onClick={disconnectCalendar}
                      className="rounded-full border-terracotta/30 text-terracotta hover:bg-terracotta/5"
                    >
                      <Unlink className="h-4 w-4 mr-1.5" /> Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    data-testid="calendar-connect-button"
                    onClick={connectCalendar}
                    className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white shadow-warm h-10 px-5"
                  >
                    <LinkIcon className="h-4 w-4 mr-1.5" /> Connect Google Calendar
                  </Button>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-brand-primary/60">
                Calendar IDs (one per line)
              </label>
              <Textarea
                data-testid="calendar-ids-input"
                rows={3}
                value={calendarIdsText}
                onChange={(e) => setCalendarIdsText(e.target.value)}
                placeholder="primary&#10;c_example@group.calendar.google.com"
                className="mt-1 rounded-xl bg-white border-brand-primary/15 font-mono text-sm break-all"
              />
              <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
                <p className="text-xs text-brand-primary/50 flex items-start sm:items-center gap-1.5 flex-1 min-w-0">
                  <Shield className="h-3 w-3 mt-0.5 sm:mt-0 shrink-0" /> <span>Use <span className="font-mono">primary</span> or specific shared calendar IDs from Google Calendar → Settings &amp; sharing → Integrate calendar.</span>
                </p>
                <Button
                  data-testid="save-calendar-ids-button"
                  onClick={saveCalendarIds}
                  className="rounded-full bg-brand-primary text-white hover:bg-brand-primary/90 h-10 px-4 shrink-0"
                >
                  Save list
                </Button>
              </div>
            </div>
            {/* OAuth diagnostic — collapsed by default. Solves the "Reconnect
                lands on 404" support flow: admin clicks "Show OAuth config",
                sees the exact redirect_uri the backend is using, copies it
                into Google Cloud Console's Authorized Redirect URIs. */}
            <details className="mt-5 group" data-testid="oauth-debug-details">
              <summary
                onClick={(e) => { if (!oauthDebug) { e.preventDefault(); loadOauthDebug(); e.currentTarget.parentElement.open = true; } }}
                className="cursor-pointer text-xs text-brand-primary/55 hover:text-brand-primary inline-flex items-center gap-1.5 select-none"
                data-testid="oauth-debug-toggle"
              >
                <Shield className="h-3 w-3" />
                Show OAuth config (for diagnosing reconnect issues)
              </summary>
              {oauthDebug && (
                <div className="mt-3 rounded-xl bg-cream border border-brand-primary/10 p-4 text-xs space-y-2" data-testid="oauth-debug-panel">
                  <div className="grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1.5 font-mono">
                    <span className="text-brand-primary/55">redirect_uri</span>
                    <span className="text-brand-primary break-all" data-testid="oauth-debug-redirect-uri">{oauthDebug.redirect_uri || "(not set)"}</span>
                    <span className="text-brand-primary/55">expected backend path</span>
                    <span className="text-brand-primary">{oauthDebug.expected_backend_path}</span>
                    <span className="text-brand-primary/55">client_id</span>
                    <span className="text-brand-primary">{oauthDebug.client_id_set ? oauthDebug.client_id_suffix : "(not set)"}</span>
                    <span className="text-brand-primary/55">scopes</span>
                    <span className="text-brand-primary">{oauthDebug.scopes?.length || 0} configured</span>
                  </div>
                  {((oauthDebug.warnings && oauthDebug.warnings.length > 0) || (oauthDebug.hints || []).filter(Boolean).length > 0) && (
                    <div className="mt-3 pt-3 border-t border-brand-primary/10 space-y-1.5">
                      {(oauthDebug.warnings || (oauthDebug.hints || []).filter(Boolean)).map((h, i) => (
                        <p key={i} className="text-terracotta flex items-start gap-1.5" data-testid={`oauth-debug-hint-${i}`}>
                          <span className="font-bold">!</span><span>{h}</span>
                        </p>
                      ))}
                    </div>
                  )}
                  <p className="text-brand-primary/50 pt-2 border-t border-brand-primary/10">
                    This redirect URI MUST also be listed in <span className="font-mono">Google Cloud Console → OAuth Client → Authorized redirect URIs</span> — character for character.
                  </p>
                </div>
              )}
            </details>

            {/* Connection state — admin observability for the credential
                lifecycle. NEVER displays raw token values; only safe
                fingerprints (length + last 4 chars). Lets an admin paste
                a production snapshot into a support thread without
                running mongo queries. */}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={loadOauthState}
                disabled={oauthStateLoading}
                data-testid="oauth-state-load"
                className="rounded-full border-brand-primary/20 text-xs"
              >
                {oauthStateLoading ? "Loading…" : (oauthState ? "Refresh connection state" : "Show connection state")}
              </Button>
              {oauthState && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={forceRefresh}
                    disabled={forceRefreshLoading}
                    data-testid="oauth-state-force-refresh"
                    className="rounded-full border-brand-primary/20 text-xs"
                  >
                    {forceRefreshLoading ? "Refreshing token…" : "Force token refresh now"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyOauthState}
                    data-testid="oauth-state-copy"
                    className="rounded-full border-brand-primary/20 text-xs"
                  >
                    Copy JSON
                  </Button>
                </>
              )}
            </div>

            {oauthState && (
              <div className="mt-3 rounded-xl bg-cream border border-brand-primary/10 p-4 text-xs space-y-3" data-testid="oauth-state-panel">
                <div className="grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1.5 font-mono">
                  <span className="text-brand-primary/55">enabled</span>
                  <span className="text-brand-primary">{String(oauthState.identity.enabled)}</span>
                  <span className="text-brand-primary/55">connected_email</span>
                  <span className="text-brand-primary break-all">{oauthState.identity.connected_email || "(none)"}</span>
                  <span className="text-brand-primary/55">refresh_token</span>
                  <span className="text-brand-primary">
                    {oauthState.refresh_token
                      ? `PRESENT — ${oauthState.refresh_token.length} chars, tail=${oauthState.refresh_token.tail}`
                      : <span className="text-terracotta">MISSING</span>}
                  </span>
                  <span className="text-brand-primary/55">access_token</span>
                  <span className="text-brand-primary">
                    {oauthState.access_token
                      ? `present — ${oauthState.access_token.length} chars, tail=${oauthState.access_token.tail}`
                      : "missing"}
                  </span>
                  <span className="text-brand-primary/55">token_expiry</span>
                  <span className="text-brand-primary">{oauthState.timestamps.token_expiry || "(none)"}</span>
                  <span className="text-brand-primary/55">last_refresh_at</span>
                  <span className="text-brand-primary">{oauthState.timestamps.last_refresh_at || "(none)"}</span>
                  <span className="text-brand-primary/55">last_refresh_attempt</span>
                  <span className="text-brand-primary">{oauthState.timestamps.last_refresh_attempt || "(none)"}</span>
                  <span className="text-brand-primary/55">last_refresh_error</span>
                  <span className={oauthState.error_state.last_refresh_error ? "text-terracotta" : "text-brand-primary"}>
                    {oauthState.error_state.last_refresh_error || "(none)"}
                  </span>
                  <span className="text-brand-primary/55">last_refresh_error_kind</span>
                  <span className={oauthState.error_state.last_refresh_error_kind === "permanent" ? "text-terracotta" : "text-brand-primary"}>
                    {oauthState.error_state.last_refresh_error_kind || "(none)"}
                  </span>
                  <span className="text-brand-primary/55">has_drive_scope</span>
                  <span className={oauthState.drive_state.has_drive_scope === false ? "text-terracotta" : "text-brand-primary"}>
                    {String(oauthState.drive_state.has_drive_scope)}
                  </span>
                  <span className="text-brand-primary/55">receipts_folder</span>
                  <span className="text-brand-primary">
                    {oauthState.drive_state.receipts_parent_folder_name || oauthState.drive_state.receipts_parent_folder_id || "(not configured)"}
                  </span>
                  <span className="text-brand-primary/55">calendar_count</span>
                  <span className="text-brand-primary">{oauthState.calendars.count}</span>
                </div>

                {oauthState.audit_trail?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-brand-primary/55 font-semibold">Recent audit events</p>
                    <div className="mt-1 max-h-40 overflow-y-auto space-y-0.5">
                      {oauthState.audit_trail.map((a, i) => (
                        <div key={i} className="font-mono text-[10px] flex gap-2 flex-wrap">
                          <span className="text-brand-primary/45">{(a.at || "").slice(0, 19).replace("T", " ")}</span>
                          <span className="text-brand-primary/85">{a.action}</span>
                          <span className="text-brand-primary/60">{a.actor_email || "—"}</span>
                          <span className="text-brand-primary/75 break-all">{a.summary}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {forceRefreshResult && (
                  <div className="rounded-md bg-white border border-brand-primary/10 p-2">
                    <p className="text-[10px] uppercase tracking-wider text-brand-primary/55 font-semibold mb-1">
                      Force-refresh result
                    </p>
                    <div className="grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1 font-mono text-[10px]">
                      <span className="text-brand-primary/55">result</span>
                      <span className={
                        forceRefreshResult.result === "ok" ? "text-sage" :
                        forceRefreshResult.result === "transient_error" ? "text-brand-primary" :
                        "text-terracotta"
                      }>
                        {forceRefreshResult.result}
                      </span>
                      {forceRefreshResult.error && (
                        <>
                          <span className="text-brand-primary/55">error</span>
                          <span className="text-terracotta break-all">{forceRefreshResult.error}</span>
                        </>
                      )}
                      <span className="text-brand-primary/55">refresh_token_rotated</span>
                      <span className="text-brand-primary">{String(forceRefreshResult.refresh_token_rotated)}</span>
                      <span className="text-brand-primary/55">access_token tail (after)</span>
                      <span className="text-brand-primary">{forceRefreshResult.after?.access_token?.tail || "(none)"}</span>
                    </div>
                  </div>
                )}

                <p className="text-brand-primary/50 pt-2 border-t border-brand-primary/10 text-[10px]">
                  No raw secrets are displayed. Token "tail" is the last 4 characters for log correlation only.
                </p>
              </div>
            )}
          </Card>

          {/* Receipts storage (Google Drive) */}
          <Card data-testid="integration-card-receipts" className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
            <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-terracotta/10 grid place-items-center">
                  <Receipt className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
                </div>
                <div>
                  <p className="font-heading text-lg text-brand-primary">Receipts storage (Google Drive)</p>
                  <p className="text-sm text-brand-primary/60">
                    {receiptStatus?.parent_folder_id ? (
                      <>
                        Saving to <span className="text-brand-primary font-medium">{receiptStatus.parent_folder_name}</span>
                        {" / Receipts / {First Name} / {Year} / {Month}"}
                      </>
                    ) : (
                      "Paste the shared Drive folder URL (or ID) where staff receipts should be filed."
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {receiptStatus?.has_drive_scope === false && (
                  <Badge className="rounded-full bg-gold/20 text-brand-primary border-0">
                    <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Drive scope missing
                  </Badge>
                )}
                {receiptStatus?.parent_folder_id && (
                  <Badge className="rounded-full bg-sage/20 text-brand-primary border-0">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Folder set
                  </Badge>
                )}
                {receiptStatus?.parent_folder_link && (
                  <a
                    href={receiptStatus.parent_folder_link}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="receipts-folder-open-link"
                    className="inline-flex items-center gap-1 text-sm text-terracotta hover:underline"
                  >
                    Open in Drive <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-xs uppercase tracking-wider text-brand-primary/60 flex items-center gap-1.5">
                <Folder className="h-3.5 w-3.5" /> Shared Drive folder URL or ID
              </label>
              <div className="flex items-stretch gap-2 flex-wrap">
                <Input
                  data-testid="receipts-folder-input"
                  value={receiptFolderInput}
                  onChange={(e) => setReceiptFolderInput(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/1AbC… or paste raw ID"
                  className="flex-1 min-w-[260px] rounded-xl bg-white border-brand-primary/15 font-mono text-sm break-all"
                />
                <Button
                  data-testid="receipts-folder-save"
                  onClick={saveReceiptFolder}
                  disabled={savingReceiptFolder || !receiptStatus?.connected}
                  className="rounded-full bg-brand-primary text-white hover:bg-brand-primary/90 h-10 px-5 shrink-0"
                >
                  {savingReceiptFolder ? "Saving…" : (receiptStatus?.parent_folder_id ? "Replace folder" : "Save folder")}
                </Button>
              </div>
              <p className="text-xs text-brand-primary/55 flex items-start gap-1.5">
                <Shield className="h-3 w-3 mt-0.5 shrink-0" />
                <span>
                  The folder must be shared with{" "}
                  <span className="font-mono">{receiptStatus?.connected_email || "the connected Google account"}</span>{" "}
                  with at least <em>Editor</em> access. The dashboard creates a <span className="font-mono">/Receipts/{"{First Name}/{Year}/{Month}"}</span> tree inside.
                </span>
              </p>
              {receiptStatus && !receiptStatus.connected && (
                <p className="text-xs text-terracotta">Connect Google Calendar above first — the same OAuth grant powers Drive uploads.</p>
              )}
              {receiptStatus?.has_drive_scope === false && (
                <p className="text-xs text-terracotta">
                  Reconnected Google but Drive scope is missing. Add{" "}
                  <code className="font-mono">.../auth/drive</code> in{" "}
                  <a href="https://console.cloud.google.com/apis/credentials/consent" target="_blank" rel="noreferrer" className="underline">
                    Cloud Console → OAuth consent screen
                  </a>{" "}
                  and reconnect.
                </p>
              )}
            </div>
          </Card>
          <PushpayCard
            integration={integrations.find((x) => x.service === "pushpay")}
            onChange={(patch) => updateIntegration("pushpay", patch)}
            onConfig={(k, v) => setConfig("pushpay", k, v)}
            onClear={() => clearCredentials("pushpay")}
          />

          <QuickBooksCard />

          <BackupsCard receiptStatus={receiptStatus} />

          {/* Other integrations */}
          {Object.keys(SERVICE_META).map((svc) => {
            const meta = SERVICE_META[svc];
            const state = integrations.find((x) => x.service === svc) ?? { enabled: false, config: {} };
            const Icon = meta.icon;
            // Env-managed CCB / Mailchimp / ESV: credentials live in the
            // production env vars (set in Emergent's deploy panel), not the
            // DB. Hide credential inputs and show a calm "Server-managed"
            // badge so admins know the integration is healthy and they don't
            // need to do anything.
            const envManaged = !!state.config?.env_managed;
            return (
              <Card key={svc} data-testid={`integration-card-${svc}`} className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
                <div className="flex items-start justify-between mb-5 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-terracotta/10 grid place-items-center">
                      <Icon className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
                    </div>
                    <div>
                      <p className="font-heading text-lg text-brand-primary flex items-center gap-2">
                        {meta.label}
                        {envManaged && (
                          <span
                            data-testid={`integration-env-managed-${svc}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-sage bg-sage/10 px-2 py-0.5 rounded-full"
                          >
                            <Shield className="h-3 w-3" /> Server-managed
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-brand-primary/60">
                        {envManaged
                          ? "Credentials are managed via deploy env vars — production redeploys keep this connected."
                          : (state.enabled
                              ? "Connected — live data will be used where available"
                              : "Not connected — placeholder data shown")}
                      </p>
                    </div>
                  </div>
                  {!envManaged && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-brand-primary/65">Enabled</span>
                      <Switch
                        data-testid={`integration-toggle-${svc}`}
                        checked={state.enabled}
                        onCheckedChange={(v) => updateIntegration(svc, { enabled: v })}
                      />
                    </div>
                  )}
                </div>
                {envManaged ? (
                  <div
                    data-testid={`integration-env-notice-${svc}`}
                    className="rounded-xl border border-sage/30 bg-sage/8 p-4 text-sm text-brand-primary/75"
                  >
                    <p className="font-medium text-brand-primary mb-1">No setup needed here.</p>
                    <p>
                      This integration's credentials are loaded from the production environment
                      variables. To rotate them, update the env vars in Emergent's deploy panel
                      and redeploy. Saved DB values (if any) are ignored while env vars are set.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid md:grid-cols-2 gap-4">
                      {meta.fields.map((f) => (
                        <IntegrationField
                          key={f.key}
                          svc={svc}
                          field={f}
                          savedValue={state.config?.[f.key] ?? ""}
                          onSave={(val) => setConfig(svc, f.key, val)}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-brand-primary/50 mt-4 flex items-center gap-1.5">
                      <Shield className="h-3 w-3" /> Changes save automatically when you click away.
                    </p>
                    {meta.helper && (
                      <p className="text-xs text-brand-primary/55 mt-2">{meta.helper}</p>
                    )}
                  </>
                )}
                {meta.test_endpoint && (
                  <div className="mt-4 pt-4 border-t border-brand-primary/10">
                    <TestConnectionButton endpoint={meta.test_endpoint} serviceKey={svc} enabled={envManaged || state.enabled} />
                  </div>
                )}
                {!envManaged && (
                  <div className="mt-3 flex justify-end">
                    <ClearCredentialsButton
                      serviceKey={svc}
                      serviceLabel={meta.label}
                      hasCredentials={meta.fields.some((f) => f.sensitive && (state.config?.[f.key] ?? "") !== "")}
                      onClear={() => clearCredentials(svc)}
                    />
                  </div>
                )}
              </Card>
            );
          })}
        </TabsContent>

        {/* Roles */}
        <TabsContent value="roles">
          <UserManagementTab
            users={users}
            me={me}
            onChangeRole={changeRole}
            onToggleKpiEdit={toggleKpiEdit}
            onToggleNewsletterEditor={toggleNewsletterEditor}
            onReload={loadAll}
          />
        </TabsContent>

        {/* Links */}
        <TabsContent value="links" className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-xl text-brand-primary">Staff quick links</h3>
              <p className="text-sm text-brand-primary/65">Shortcuts that appear on the Staff Directory. All links open in a new tab — if staff are already signed in to the tool, they'll land directly in their account.</p>
            </div>
            <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-link-button" className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-10 px-5 shadow-warm">
                  <Plus className="h-4 w-4 mr-1.5" /> Add link
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">New quick link</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <Input data-testid="link-title-input" placeholder="Title" value={newLink.title} onChange={(e) => setNewLink({ ...newLink, title: e.target.value })} />
                  <Input data-testid="link-url-input" placeholder="https://…" value={newLink.url} onChange={(e) => setNewLink({ ...newLink, url: e.target.value })} />
                  <Input data-testid="link-icon-input" placeholder="Icon key (optional): mail | calendar | hand-coins | users | send | globe" value={newLink.icon} onChange={(e) => setNewLink({ ...newLink, icon: e.target.value })} />
                  <Input data-testid="link-category-input" placeholder="Category (optional)" value={newLink.category} onChange={(e) => setNewLink({ ...newLink, category: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button data-testid="link-submit-button" onClick={addLink} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden" data-testid="quick-links-admin-list">
            <ul className="divide-y divide-brand-primary/10">
              {links.map((ql, idx) => (
                <QuickLinkRow
                  key={ql.id}
                  link={ql}
                  isFirst={idx === 0}
                  isLast={idx === links.length - 1}
                  onMove={(dir) => moveLink(ql.id, dir)}
                  onSave={(patch) => saveLinkEdit(ql.id, patch)}
                  onDelete={() => delLink(ql.id)}
                />
              ))}
              {links.length === 0 && (
                <li className="p-6 text-sm text-brand-primary/55 text-center">No quick links yet.</li>
              )}
            </ul>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// --- QuickBooks Online card ---
function QuickBooksCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/oauth/quickbooks/status");
      setStatus(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.get("/oauth/quickbooks/login");
      if (data?.authorization_url) window.location.href = data.authorization_url;
      else { toast.error("QuickBooks login URL missing"); setBusy(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start QuickBooks login");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect QuickBooks? Finance data will show as unconnected until you reconnect.")) return;
    setBusy(true);
    try {
      await api.post("/oauth/quickbooks/disconnect");
      toast.success("QuickBooks disconnected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  const connected = !!status?.connected;
  const env = status?.env || "sandbox";
  const realmId = status?.realm_id;
  const lastRefreshAt = status?.last_refresh_at;
  const errorKind = status?.last_refresh_error_kind;
  const lastError = status?.last_refresh_error;

  return (
    <Card data-testid="integration-card-quickbooks" className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-terracotta/10 grid place-items-center">
            <Landmark className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
          </div>
          <div>
            <p className="font-heading text-lg text-brand-primary">QuickBooks Online (Finance)</p>
            <p className="text-sm text-brand-primary/60">
              {connected
                ? <>Read-only income &amp; expense per ministry, rolled up by QuickBooks Class. <span className="text-brand-primary">{env}</span></>
                : "Not connected — /finance shows a disconnected banner until you authorize."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge data-testid="quickbooks-env-managed-badge" className="rounded-full bg-sage/20 text-brand-primary border-0">
            <Shield className="h-3.5 w-3.5 mr-1" /> Server-managed
          </Badge>
          {connected && (
            <Badge data-testid="quickbooks-connected-badge" className="rounded-full bg-sage/20 text-brand-primary border-0">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Connected
            </Badge>
          )}
          {errorKind === "permanent" && (
            <Badge className="rounded-full bg-terracotta/15 text-terracotta border-0">
              <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Refresh failed
            </Badge>
          )}
          {connected ? (
            <Button
              data-testid="quickbooks-disconnect-btn"
              onClick={disconnect}
              disabled={busy}
              className="rounded-full bg-white text-brand-primary border border-brand-primary/15 hover:bg-brand-primary/5 h-9 px-4"
            >
              <Unlink className="h-4 w-4 mr-1.5" /> Disconnect
            </Button>
          ) : (
            <Button
              data-testid="quickbooks-connect-btn"
              onClick={connect}
              disabled={busy || loading}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-9 px-4"
            >
              <PlugZap className="h-4 w-4 mr-1.5" />
              {busy ? "Redirecting…" : "Connect QuickBooks"}
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          )}
        </div>
      </div>

      <div data-testid="quickbooks-env-managed-notice" className="rounded-xl bg-sage/10 border border-sage/30 p-4 text-sm text-brand-primary/80 flex items-start gap-3">
        <Shield className="h-4 w-4 mt-0.5 text-sage shrink-0" />
        <div className="min-w-0">
          <p className="font-medium text-brand-primary">Credentials are managed by ops via backend environment variables.</p>
          <p className="mt-1 text-xs">
            <code className="font-mono">QBO_CLIENT_ID</code>, <code className="font-mono">QBO_CLIENT_SECRET</code>, <code className="font-mono">QBO_ENV</code>, and <code className="font-mono">QBO_REDIRECT_URI</code> are set on the server. Refresh &amp; access tokens are AES-GCM encrypted at rest. All QBO calls are strictly read-only.
          </p>
        </div>
      </div>

      {connected && (
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded-xl bg-brand-primary/5 border border-brand-primary/10 p-3">
            <p className="text-[10px] uppercase tracking-wider text-brand-primary/55">Environment</p>
            <p data-testid="quickbooks-env" className="text-brand-primary mt-1 font-medium">{env}</p>
          </div>
          <div className="rounded-xl bg-brand-primary/5 border border-brand-primary/10 p-3">
            <p className="text-[10px] uppercase tracking-wider text-brand-primary/55">QBO Realm ID</p>
            <p data-testid="quickbooks-realm" className="text-brand-primary mt-1 font-mono text-xs break-all">{realmId || "—"}</p>
          </div>
          <div className="rounded-xl bg-brand-primary/5 border border-brand-primary/10 p-3">
            <p className="text-[10px] uppercase tracking-wider text-brand-primary/55">Last token refresh</p>
            <p data-testid="quickbooks-last-refresh" className="text-brand-primary mt-1 text-xs">
              {lastRefreshAt ? new Date(lastRefreshAt).toLocaleString() : "—"}
            </p>
          </div>
        </div>
      )}

      {errorKind === "permanent" && lastError && (
        <div className="mt-4 rounded-xl bg-terracotta/10 border border-terracotta/25 p-4 text-sm text-brand-primary/80">
          <p className="font-medium text-terracotta">Last refresh failed permanently:</p>
          <p className="font-mono text-xs mt-1 break-all">{lastError}</p>
          <p className="mt-2 text-xs">Disconnect and click Connect QuickBooks again to re-authorize.</p>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <Link
          to="/finance"
          className="inline-flex items-center gap-1 text-sm text-terracotta hover:underline"
          data-testid="quickbooks-open-finance-link"
        >
          Open Finance dashboard <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </Card>
  );
}


// --- Backups card ---
function BackupsCard({ receiptStatus }) {
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get("/backup/history");
      setHistory(data.history || []);
    } catch {
      // silent
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/backup/run");
      if (data.ok) {
        toast.success(`Backup uploaded (${(data.size_bytes / 1024).toFixed(1)} KB)`);
      } else if (data.skipped) {
        toast.warning(data.reason || "Backup skipped");
      } else {
        toast.error("Backup failed: " + (data.error || "unknown"));
      }
      await loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backup failed");
    } finally {
      setRunning(false);
    }
  };

  const folderConfigured = !!receiptStatus?.parent_folder_id;

  return (
    <Card data-testid="integration-card-backups" className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-terracotta/10 grid place-items-center">
            <Shield className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
          </div>
          <div>
            <p className="font-heading text-lg text-brand-primary">Nightly backups</p>
            <p className="text-sm text-brand-primary/60">
              Snapshot of users, KPIs, announcements, receipts, and integration configs.
              {receiptStatus?.parent_folder_name ? (
                <> Stored in <span className="font-medium text-brand-primary">{receiptStatus.parent_folder_name}</span> at <code className="font-mono text-xs">/Backups/{"{date}"}/</code>.</>
              ) : (
                <> Will be stored alongside Receipts once the parent folder is configured above.</>
              )}
              {" "}Sensitive credentials are redacted before upload.
            </p>
          </div>
        </div>
        <Button
          onClick={runNow}
          disabled={running || !folderConfigured}
          data-testid="backup-run-now"
          className="rounded-full bg-brand-primary hover:bg-brand-primary/90 text-white h-10 px-5"
        >
          {running ? "Backing up…" : "Run backup now"}
        </Button>
      </div>
      {!folderConfigured && (
        <p className="text-xs text-terracotta">
          Set the receipts/storage parent folder above before running a backup — both features share the same Drive folder.
        </p>
      )}
      {history.length > 0 && (
        <div className="mt-2 border-t border-brand-primary/10 pt-3">
          <p className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-medium mb-2">Recent backups</p>
          <ul className="space-y-1.5 text-xs" data-testid="backup-history-list">
            {history.slice(0, 5).map((h, i) => (
              <li key={i} className="flex items-center justify-between gap-3 flex-wrap">
                <span className={h.ok ? "text-brand-primary/75" : "text-terracotta"}>
                  {new Date(h.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  {" · "}
                  {h.ok ? `${Math.round((h.size_bytes || 0) / 1024)} KB` : (h.error || "failed")}
                </span>
                {h.drive_view_link && (
                  <a href={h.drive_view_link} target="_blank" rel="noreferrer" className="text-terracotta hover:underline">
                    Open in Drive
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {loadingHistory && history.length === 0 && (
        <p className="text-xs text-brand-primary/45 mt-2">Loading history…</p>
      )}
    </Card>
  );
}

// --- Pushpay card ---
function PushpayCard({ integration, onChange, onConfig, onClear }) {
  const state = integration ?? { enabled: false, config: {} };
  const cfg = state.config || {};
  const envManaged = !!cfg.env_managed;
  const connected = envManaged || (state.enabled && cfg.client_id && cfg.client_secret);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [local, setLocal] = useState({
    client_id: cfg.client_id || "",
    client_secret: cfg.client_secret || "",
    organization_key: cfg.organization_key || "",
    sandbox: !!cfg.sandbox,
    guests_path: cfg.guests_path || "",
    volunteers_path: cfg.volunteers_path || "",
    small_groups_path: cfg.small_groups_path || "",
  });

  useEffect(() => {
    setLocal({
      client_id: cfg.client_id || "",
      client_secret: cfg.client_secret || "",
      organization_key: cfg.organization_key || "",
      sandbox: !!cfg.sandbox,
      guests_path: cfg.guests_path || "",
      volunteers_path: cfg.volunteers_path || "",
      small_groups_path: cfg.small_groups_path || "",
    });
    // eslint-disable-next-line
  }, [integration?.service, cfg.client_id, cfg.organization_key, cfg.sandbox]);

  const saveField = (key, { sensitive = false } = {}) => {
    const next = local[key];
    // Guardrail: never auto-overwrite a stored sensitive value with a blank.
    // Admin must use the explicit "Clear credentials" action to erase.
    if (sensitive && (next === "" || next == null) && cfg[key]) {
      toast.info(`To erase this ${key.replace(/_/g, " ")}, use "Clear credentials".`);
      setLocal((prev) => ({ ...prev, [key]: cfg[key] }));
      return;
    }
    if ((next ?? "") !== (cfg[key] ?? "")) onConfig(key, next);
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/integrations/pushpay/test");
      setTestResult(data);
      if (data.ok) toast.success("Pushpay connection OK");
      else toast.error(data.error || "Test failed");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Test failed";
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card data-testid="integration-card-pushpay" className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-terracotta/10 grid place-items-center">
            <HandCoins className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
          </div>
          <div>
            <p className="font-heading text-lg text-brand-primary">Pushpay</p>
            <p className="text-sm text-brand-primary/60">
              {envManaged
                ? <>Configured via server environment · production · giving only</>
                : connected
                ? <>OAuth2 client credentials stored · {local.sandbox ? "sandbox" : "production"}</>
                : "Not connected — KPI cards will show manual/placeholder data."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {envManaged && (
            <Badge data-testid="pushpay-env-managed-badge" className="rounded-full bg-sage/20 text-brand-primary border-0">
              <Shield className="h-3.5 w-3.5 mr-1" /> Server-managed
            </Badge>
          )}
          {connected && !envManaged && (
            <Badge className="rounded-full bg-sage/20 text-brand-primary border-0">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Credentials saved
            </Badge>
          )}
          {!envManaged && (
            <label className="flex items-center gap-2 text-sm text-brand-primary/75 px-3 py-1 rounded-full border border-brand-primary/15">
              Sandbox
              <Switch
                data-testid="pushpay-sandbox-switch"
                checked={local.sandbox}
                onCheckedChange={(v) => { setLocal({ ...local, sandbox: v }); onConfig("sandbox", v); }}
              />
            </label>
          )}
          {!envManaged && (
            <label className="flex items-center gap-2 text-sm text-brand-primary/75">
              Enabled
              <Switch
                data-testid="pushpay-enabled-switch"
                checked={state.enabled}
                onCheckedChange={(v) => onChange({ enabled: v })}
              />
            </label>
          )}
          <Button
            data-testid="pushpay-test-button"
            onClick={runTest}
            disabled={testing || (!envManaged && (!local.client_id || !local.client_secret || !local.organization_key))}
            className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-9 px-4"
          >
            <TestTube2 className={`h-4 w-4 mr-1.5 ${testing ? "animate-pulse" : ""}`} /> Test connection
          </Button>
        </div>
      </div>

      {envManaged ? (
        <div data-testid="pushpay-env-managed-notice" className="rounded-xl bg-sage/10 border border-sage/30 p-4 text-sm text-brand-primary/80 flex items-start gap-3">
          <Shield className="h-4 w-4 mt-0.5 text-sage shrink-0" />
          <div>
            <p className="font-medium text-brand-primary">Credentials are managed by ops via backend environment variables.</p>
            <p className="mt-1 text-xs">
              <code className="font-mono">PUSHPAY_CLIENT_ID</code>, <code className="font-mono">PUSHPAY_CLIENT_SECRET</code>, <code className="font-mono">PUSHPAY_AUTH_URL</code>, and <code className="font-mono">PUSHPAY_API_BASE</code> are set on the server. They are never exposed to this dashboard. To rotate, update the deployment environment.
            </p>
          </div>
        </div>
      ) : (
        <>
        {/* Core credential inputs */}
        <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-brand-primary/60">Client ID</label>
          <Input
            data-testid="pushpay-client-id"
            placeholder="Issued by Pushpay Dev Relations"
            value={local.client_id}
            onChange={(e) => setLocal({ ...local, client_id: e.target.value })}
            onBlur={() => saveField("client_id")}
            className="mt-1 rounded-xl bg-white border-brand-primary/15 font-mono text-sm"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-brand-primary/60">Client Secret</label>
          <Input
            data-testid="pushpay-client-secret"
            type="password"
            placeholder="••••••••"
            value={local.client_secret}
            onChange={(e) => setLocal({ ...local, client_secret: e.target.value })}
            onBlur={() => saveField("client_secret", { sensitive: true })}
            className="mt-1 rounded-xl bg-white border-brand-primary/15 font-mono text-sm"
          />
          {cfg.client_secret && (
            <p className="text-[10px] text-brand-primary/45 mt-1">
              Saved value is protected — leaving this field blank won't erase it.
            </p>
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-brand-primary/60">Organization Key</label>
          <Input
            data-testid="pushpay-organization-key"
            placeholder="e.g. MTIzOi1Yb1ByLWxtakFoMVdEaUlrWFpIdnhfODd0NA"
            value={local.organization_key}
            onChange={(e) => setLocal({ ...local, organization_key: e.target.value })}
            onBlur={() => saveField("organization_key")}
            className="mt-1 rounded-xl bg-white border-brand-primary/15 font-mono text-sm"
          />
          <p className="text-xs text-brand-primary/50 mt-1">
            Pushpay Admin → Settings → API & Integrations → Organization Key. Case-sensitive.
          </p>
        </div>
      </div>
      </>
      )}

      {/* Test result */}
      {testResult && (
        <div
          data-testid="pushpay-test-result"
          className={`mt-5 rounded-xl p-4 text-sm flex items-start gap-3 ${
            testResult.ok ? "bg-sage/15 text-brand-primary" : "bg-peach/25 text-brand-primary"
          }`}
        >
          {testResult.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-sage shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 text-terracotta shrink-0" />}
          <div className="flex-1">
            {testResult.ok ? (
              <p><strong>Connection OK</strong> · token minted · in-scope orgs visible · payments probe returned 200.</p>
            ) : (
              <>
                <p className="font-medium">Test failed</p>
                <p className="mt-1">{testResult.error || "See server logs"}</p>
                <ul className="mt-1 text-xs opacity-80 list-disc list-inside">
                  <li>Token obtained: {String(!!testResult.token_ok)}</li>
                  <li>in-scope orgs: {String(!!testResult.in_scope_ok)}</li>
                  <li>payments probe: {String(!!testResult.payments_ok)}</li>
                </ul>
              </>
            )}
          </div>
        </div>
      )}

      {/* Advanced endpoints — hidden in env-managed mode (giving-only scope) */}
      {!envManaged && (
      <div className="mt-5 pt-5 border-t border-brand-primary/10">
        <button
          onClick={() => setShowAdvanced((v) => !v)}
          data-testid="pushpay-toggle-advanced"
          className="text-xs uppercase tracking-wider text-brand-primary/60 hover:text-terracotta"
        >
          {showAdvanced ? "Hide" : "Show"} advanced endpoint paths (Guests / Volunteers / Small Groups)
        </button>
        {showAdvanced && (
          <div className="grid md:grid-cols-3 gap-4 mt-3">
            {[
              { key: "guests_path", label: "Guests endpoint", placeholder: "/v1/organization/{organizationKey}/community/members" },
              { key: "volunteers_path", label: "Volunteers endpoint", placeholder: "/v1/organization/{organizationKey}/scheduler/assignments" },
              { key: "small_groups_path", label: "Small Groups endpoint", placeholder: "/v1/organization/{organizationKey}/groups/attendance" },
            ].map((f) => (
              <div key={f.key}>
                <label className="text-xs uppercase tracking-wider text-brand-primary/60">{f.label}</label>
                <Input
                  data-testid={`pushpay-${f.key}`}
                  placeholder={f.placeholder}
                  value={local[f.key] || ""}
                  onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })}
                  onBlur={() => saveField(f.key)}
                  className="mt-1 rounded-xl bg-white border-brand-primary/15 font-mono text-xs"
                />
              </div>
            ))}
            <p className="md:col-span-3 text-xs text-brand-primary/55">
              Confirm these paths in Pushpay's Swagger UI (<a href="https://api.pushpay.com/swagger" target="_blank" rel="noreferrer" className="text-terracotta hover:underline">api.pushpay.com/swagger</a>) or with your Dev Relations contact. Leave blank to use defaults. Paths must include <code className="font-mono">{"{organizationKey}"}</code>.
            </p>
          </div>
        )}
      </div>
      )}

      <div className="mt-4 pt-4 border-t border-brand-primary/10 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-xs text-brand-primary/50 flex items-center gap-1.5">
          <Shield className="h-3 w-3" /> Credentials are stored server-side only. Never exposed to this dashboard.
        </p>
        {!envManaged && (
          <ClearCredentialsButton
            serviceKey="pushpay"
            serviceLabel="Pushpay"
            hasCredentials={!!(cfg.client_secret || cfg.client_id)}
            onClear={onClear}
          />
        )}
      </div>
    </Card>
  );
}


// --- Individual integration field (decoupled from global `integrations` state
//     so fast typing doesn't trigger stale-closure races). Saves on blur or Enter.
function IntegrationField({ svc, field, savedValue, onSave }) {
  const [draft, setDraft] = useState(savedValue);

  // Re-sync draft when the server-side value actually changes (after successful save or reload).
  useEffect(() => {
    setDraft(savedValue);
  }, [savedValue, svc, field.key]);

  const commit = () => {
    const next = typeof draft === "string" ? draft.trim() : draft;
    // Guardrail: never auto-overwrite an already-stored credential with an
    // empty value. Admins must use "Clear credentials" to erase. Prevents
    // accidental blanks from wiping a live Mailchimp / Pushpay / CCB key.
    if (field.sensitive && (next === "" || next == null) && savedValue) {
      toast.info(`To erase this ${field.label.toLowerCase()}, use "Clear credentials".`);
      setDraft(savedValue);
      return;
    }
    if (next !== (savedValue ?? "")) {
      onSave(next);
    }
  };

  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-brand-primary/60">{field.label}</label>
      <Input
        data-testid={`integration-${svc}-${field.key}`}
        type={field.sensitive ? "password" : "text"}
        placeholder={field.placeholder}
        value={draft ?? ""}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        className="mt-1 rounded-xl bg-white border-brand-primary/15"
      />
      {field.sensitive && savedValue && (
        <p className="text-[10px] text-brand-primary/45 mt-1">
          Saved value is protected — leaving this field blank won't erase it.
        </p>
      )}
    </div>
  );
}


// --- Generic test-connection button (used by integrations that expose a test endpoint) ---
function TestConnectionButton({ endpoint, serviceKey, enabled }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setTesting(true);
    setResult(null);
    try {
      const { data } = await api.post(endpoint);
      setResult(data);
      if (data?.ok) toast.success(`${serviceKey} connection OK`);
      else toast.error(data?.detail || "Connection failed");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unable to test";
      setResult({ ok: false, detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-brand-primary/60 font-semibold">Verify credentials</p>
        {result && (
          <p
            className={`text-xs mt-1 ${result.ok ? "text-sage" : "text-terracotta"}`}
            data-testid={`test-${serviceKey}-result`}
          >
            {result.ok ? <CheckCircle2 className="h-3 w-3 inline mr-1" /> : <AlertTriangle className="h-3 w-3 inline mr-1" />}
            {result.detail || (result.ok ? "Connected" : "Failed")}
          </p>
        )}
      </div>
      <Button
        data-testid={`test-${serviceKey}-button`}
        disabled={testing || !enabled}
        onClick={run}
        variant="outline"
        className="rounded-full border-brand-primary/15"
      >
        {testing ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <TestTube2 className="h-4 w-4 mr-1.5" />}
        Test connection
      </Button>
    </div>
  );
}


// --- Admin-only "Clear credentials" button with explicit confirm. Maps to
//     DELETE /api/integrations/{svc}/credentials?confirm=1 — the only code
//     path that is allowed to wipe stored keys.
function ClearCredentialsButton({ serviceKey, serviceLabel, hasCredentials, onClear }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await onClear();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
      <AlertDialogTrigger asChild>
        <Button
          data-testid={`clear-${serviceKey}-button`}
          variant="outline"
          disabled={!hasCredentials}
          className="rounded-full border-terracotta/30 text-terracotta hover:bg-terracotta/5 disabled:opacity-40"
        >
          <Trash2 className="h-4 w-4 mr-1.5" />
          Clear credentials
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`clear-${serviceKey}-confirm`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Clear {serviceLabel} credentials?</AlertDialogTitle>
          <AlertDialogDescription>
            This will erase the stored API keys / tokens for <strong>{serviceLabel}</strong> and
            disable the integration. Dashboards that rely on {serviceLabel} will revert to
            placeholder / empty data until someone re-enters the credentials.
            This action is explicit and cannot be caused by an accidental blank save.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`clear-${serviceKey}-cancel`} disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid={`clear-${serviceKey}-confirm-button`}
            onClick={(e) => { e.preventDefault(); run(); }}
            disabled={busy}
            className="bg-terracotta hover:bg-terracotta-hover text-white"
          >
            {busy ? "Clearing..." : "Yes, clear credentials"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


// --- Quick link row with inline edit + reorder ---
function QuickLinkRow({ link, isFirst, isLast, onMove, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: link.title,
    url: link.url,
    icon: link.icon || "",
    category: link.category || "",
  });

  useEffect(() => {
    setForm({
      title: link.title,
      url: link.url,
      icon: link.icon || "",
      category: link.category || "",
    });
  }, [link.id, link.title, link.url, link.icon, link.category]);

  const commit = () => {
    if (!form.title.trim() || !form.url.trim()) {
      toast.error("Title and URL required");
      return;
    }
    onSave({
      title: form.title.trim(),
      url: form.url.trim(),
      icon: form.icon.trim(),
      category: form.category.trim(),
    });
    setEditing(false);
  };

  if (editing) {
    return (
      <li className="p-5" data-testid={`link-row-${link.id}`}>
        <div className="grid md:grid-cols-2 gap-3">
          <Input
            data-testid={`link-edit-title-${link.id}`}
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <Input
            data-testid={`link-edit-url-${link.id}`}
            placeholder="https://…"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
          />
          <Input
            data-testid={`link-edit-icon-${link.id}`}
            placeholder="Icon key (optional)"
            value={form.icon}
            onChange={(e) => setForm({ ...form, icon: e.target.value })}
          />
          <Input
            data-testid={`link-edit-category-${link.id}`}
            placeholder="Category (optional)"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          />
        </div>
        <div className="flex items-center justify-end gap-2 mt-3">
          <Button
            variant="ghost"
            onClick={() => setEditing(false)}
            className="rounded-full text-brand-primary/70"
            data-testid={`link-edit-cancel-${link.id}`}
          >
            Cancel
          </Button>
          <Button
            onClick={commit}
            className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white"
            data-testid={`link-edit-save-${link.id}`}
          >
            Save changes
          </Button>
        </div>
      </li>
    );
  }

  return (
    <li
      className="p-5 flex items-center gap-4 hover:bg-cream/50 transition"
      data-testid={`link-row-${link.id}`}
    >
      <div className="flex flex-col shrink-0">
        <button
          onClick={() => onMove(-1)}
          disabled={isFirst}
          data-testid={`link-move-up-${link.id}`}
          className="h-6 w-6 rounded-md grid place-items-center text-brand-primary/55 hover:text-terracotta disabled:opacity-25 disabled:cursor-not-allowed"
          aria-label="Move up"
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onMove(1)}
          disabled={isLast}
          data-testid={`link-move-down-${link.id}`}
          className="h-6 w-6 rounded-md grid place-items-center text-brand-primary/55 hover:text-terracotta disabled:opacity-25 disabled:cursor-not-allowed"
          aria-label="Move down"
        >
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium text-brand-primary">{link.title}</p>
          {link.category && (
            <Badge className="rounded-full bg-sage/20 text-brand-primary border-0 text-[10px] uppercase tracking-wider">
              {link.category}
            </Badge>
          )}
        </div>
        <a
          href={link.url}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-terracotta hover:underline inline-flex items-center gap-1 mt-0.5"
          data-testid={`link-visit-${link.id}`}
        >
          {link.url}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={() => setEditing(true)}
          data-testid={`link-edit-${link.id}`}
          className="h-9 w-9 rounded-lg grid place-items-center text-brand-primary/60 hover:text-terracotta hover:bg-cream"
          aria-label="Edit link"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          data-testid={`link-delete-${link.id}`}
          className="h-9 w-9 rounded-lg grid place-items-center text-brand-primary/60 hover:text-terracotta hover:bg-cream"
          aria-label="Delete link"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}
